import * as React from 'preact/compat'
import Cropper from 'react-easy-crop'
import type { Point, Area } from 'react-easy-crop/types'
import Modal from './Modal'
import buttonsCss from '../../css/buttons.pcss.json'

export interface ImageCropProps {
  image: string,
  size: Array<number>,
  onClose: () => void,
  onCropComplete: (imgData: string) => void,
  round?: boolean,
  hideGrid?: boolean
}

interface ImageCropState {
  crop: Point,
  zoom: number,
  croppedAreaPixels: Area | null,
  objectFit: 'contain' | 'horizontal-cover' | 'vertical-cover' | 'auto-cover'
}

export default class ImageCrop extends React.Component<ImageCropProps, ImageCropState> {
  state: ImageCropState = {
    crop: {
      x: 0,
      y: 0,
    },
    zoom: 1,
    croppedAreaPixels: null,
    objectFit: 'horizontal-cover'
  }

  modal = React.createRef<Modal>()

  onCropChange = (crop: Point) => {
    this.setState({ crop })
  }

  onZoomChange = (zoom: number) => {
    this.setState({ zoom })
  }

  zoomChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ zoom: parseFloat(e.currentTarget.value) })
  }

  onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
    this.setState({
      croppedAreaPixels
    })
  }

  static createImage = (url: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = (error: Event) => reject(error)
      image.crossOrigin = "Anonymous";
      image.src = url
    })

  getCroppedImg = async (imageSrc: string, pixelCrop: Area) => {
    const size = this.props.size
    const image = await ImageCrop.createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d', { alpha: false })

    if (!ctx) {
      return null
    }

    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'medium'

    canvas.width = image.width
    canvas.height = image.height
    ctx.drawImage(image, 0, 0)

    const data = ctx.getImageData(
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height
    )

    canvas.width = size[0]
    canvas.height = size[1]

    const imageBitmap = await window.createImageBitmap(data, 0, 0, data.width, data.height)
    // ctx.scale(size[0] / data.width, size[1] / data.height)
    ctx.drawImage(imageBitmap, 0, 0, canvas.width, canvas.height)

    return new Promise<string>((resolve) => {
      canvas.toBlob((file) => {
        resolve(URL.createObjectURL(file))
      }, 'image/jpeg')
    })
  }

  endCrop = async () => {
    const croppedImage = await this.getCroppedImg(
      this.props.image,
      this.state.croppedAreaPixels,
    )
    await this.modal.current.close()
    this.props.onCropComplete(croppedImage)
  }

  componentWillMount() {
    const img = new Image()
    img.src = this.props.image
    img.onload = () => {
      let objectFit: 'horizontal-cover' | 'vertical-cover' = 'horizontal-cover'
      if (img.width > img.height) {
        objectFit = 'vertical-cover'
      }

      this.setState({
        objectFit
      })
    }
  }

  render() {
    const { size, image, round, hideGrid } = this.props
    const { objectFit, crop, zoom } = this.state
    const aspect = size[0] / size[1]
    const bodyStyles = window.getComputedStyle(document.body)
    const horizontalPadding = parseInt(bodyStyles.getPropertyValue('--section-horizontal-padding'))

    const maxWidth = Math.min(window.innerWidth - (6 + horizontalPadding * 4), 1200)
    const maxHeight = Math.min(window.innerHeight / 2, 720)
    let width = size[0]
    let height = size[1]
    if (size[0] > maxWidth) {
      width = maxWidth
      height = width / aspect
    }

    if (height > maxHeight) {
      height = maxHeight
      width = height * aspect
    }

    return <Modal ref={this.modal} showClose={true} onClose={this.props.onClose} disableOutsideClose>
      <div
        style={{
          width: `${width}px`,
          height: `${height}px`,
          position: 'relative',
          overflow: 'hidden',
          marginTop: '10px',
          marginBottom: '16px'
        }}
      >
        <Cropper
          image={image}
          crop={crop}
          zoom={zoom}
          cropShape={round ? 'round' : 'rect'}
          showGrid={!hideGrid}
          aspect={aspect}
          objectFit={objectFit}
          onCropChange={this.onCropChange}
          onCropComplete={this.onCropComplete}
          onZoomChange={this.onZoomChange}
        />
      </div>
      <input
        type='range'
        value={zoom}
        min={1}
        max={3}
        step={0.1}
        onChange={this.zoomChangeHandler}
        style={{ width: '100%' }}
      />
      <button type="button" className={buttonsCss['primary']} onClick={this.endCrop}>
        Обрезать
      </button>
    </Modal>
  }
}
